import { gql } from '@apollo/client';
import { useState } from 'react';
import DatoMetaTags from '~/components/DatoCms/DatoMetaTags';
import BlogContent, { blogPostsPerPage } from '~/components/_blog/BlogContent';
import config from '~/config';
import type {
  getAllBlogCategories,
  getAllBlogCategories_allBlogCategories,
} from '~/types/generated/cms/getAllBlogCategories';
import type { getAllTags_allBlogTags } from '~/types/generated/cms/getAllTags';
import type {
  getBlogCategoryBySlug,
  getBlogCategoryBySlugVariables,
  getBlogCategoryBySlug_allBlogCategories,
} from '~/types/generated/cms/getBlogCategoryBySlug';
import type {
  getPostsByCategoryIdWithPagination,
  getPostsByCategoryIdWithPaginationVariables,
  getPostsByCategoryIdWithPagination_allBlogPosts,
} from '~/types/generated/cms/getPostsByCategoryIdWithPagination';
import { BlogPostFragment, SeoMetaTagsFragment } from 'src/datoCMS/fragments/fragments';
import { getDatoClient } from 'src/datoCMS/datoCmsClient';
import { allTags, GET_ALL_BLOG_CATEGORIES } from '../..';
import type { GetStaticProps } from 'next';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';

const client = getDatoClient();

const GET_BLOG_CATEGORY_BY_SLUG = gql`
  ${SeoMetaTagsFragment}
  query getBlogCategoryBySlug($slug: String!) {
    allBlogCategories(filter: { slug: { eq: $slug } }) {
      id
      name
      slug
      seo: _seoMetaTags {
        ...SeoMetaTagsFragment
      }
    }
  }
`;

const GET_POSTS_BY_CATEGORY_ID_WITH_PAGINATION = gql`
  ${BlogPostFragment}
  query getPostsByCategoryIdWithPagination(
    $categoryId: ItemId!
    $first: IntType!
    $skip: IntType!
  ) {
    allBlogPosts(
      filter: { category: { eq: $categoryId } }
      first: $first
      skip: $skip
      orderBy: firstPublishedAt_DESC
    ) {
      ...BlogPostFragment
    }
    _allBlogPostsMeta(filter: { category: { eq: $categoryId } }) {
      count
    }
  }
`;

const categoryBySlug = async (
  slug: string,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<getBlogCategoryBySlug_allBlogCategories | undefined> => {
  const { data } = await client.query<getBlogCategoryBySlug, getBlogCategoryBySlugVariables>({
    query: GET_BLOG_CATEGORY_BY_SLUG,
    variables: { slug: slug },
  });

  return data.allBlogCategories[0];
};

const allCategories = async (): Promise<readonly getAllBlogCategories_allBlogCategories[]> => {
  const { data } = await client.query<getAllBlogCategories>({ query: GET_ALL_BLOG_CATEGORIES });

  return data.allBlogCategories;
};

export interface PageProps {
  tags: readonly getAllTags_allBlogTags[];
  category: getBlogCategoryBySlug_allBlogCategories;
  posts: readonly getPostsByCategoryIdWithPagination_allBlogPosts[];
  postsCount: number;
}

export const postsByCategoryIdWithPagination = async (
  categoryId: string,
  skip: number,
  client: ApolloClient<NormalizedCacheObject>,
) => {
  const { data } = await client.query<
    getPostsByCategoryIdWithPagination,
    getPostsByCategoryIdWithPaginationVariables
  >({
    query: GET_POSTS_BY_CATEGORY_ID_WITH_PAGINATION,
    variables: { categoryId, first: blogPostsPerPage, skip },
  });

  return data;
};

const CategorySlug: React.FC<PageProps> = ({ tags, category, posts, postsCount }) => {
  const [allPosts, setAllPosts] = useState(posts);

  const fetchNextPosts = async (page: number) => {
    const skip = page * blogPostsPerPage;
    const { allBlogPosts: nextPosts } = await postsByCategoryIdWithPagination(
      category.id,
      skip,
      client,
    );

    setAllPosts([...allPosts, ...nextPosts]);
  };

  const breadCrumbs = [
    {
      name: 'Blog',
      path: '/blog',
    },
    {
      name: category.name!,
      path: `/blog/category/${category.slug!}`,
    },
  ];

  const canonicalUrl = `https://energiebespaarders.nl/blog/category/${category.slug}`;

  return (
    <>
      <DatoMetaTags metaTags={category.seo} canonicalUrl={canonicalUrl} />
      <BlogContent
        fetchNextPosts={fetchNextPosts}
        breadCrumbs={breadCrumbs}
        heading={{
          text: 'Alle artikelen over',
          heading: { text: category.name!.toLowerCase(), as: 'h1' },
        }}
        tags={tags}
        posts={allPosts}
        postsCount={postsCount}
        metaTags={category.seo}
      />
    </>
  );
};

export default CategorySlug;

export const getStaticProps: GetStaticProps<PageProps> = async ({ params, preview }) => {
  if (!params) return { notFound: true };

  const client = getDatoClient(preview);
  const category = await categoryBySlug(params.slug as string, client);
  if (!category) return { notFound: true };

  const {
    allBlogPosts,
    _allBlogPostsMeta: { count },
  } = await postsByCategoryIdWithPagination(category.id, 0, client);

  return {
    props: {
      category,
      tags: await allTags(client),
      posts: allBlogPosts,
      postsCount: count,
    },
    revalidate: 60,
  };
};

export const getStaticPaths = async () => {
  if (config.build.skipStaticGeneration) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const categories = (await allCategories()).map(c => ({ params: { slug: c.slug! } }));

  return {
    paths: categories,
    fallback: 'blocking',
  };
};
